import { api } from './api';

export interface SendUserFeedbackFile {
  fileName: string;
  mimeType: string;
  fileSizeBytes: number;
  fileData: string;
}

export interface SendUserFeedbackInput {
  subject: string;
  description: string;
  platform: string;
  deviceModel: string | null;
  files?: SendUserFeedbackFile[];
}

export interface UserFeedbackFileMeta {
  fileId: number;
  fileName: string;
  mimeType: string;
  fileSizeBytes: number;
}

export interface UserFeedbackRecord {
  feedbackId: number;
  submitterName: string;
  subject: string;
  description: string;
  submittedAt: string;
  platform: string;
  deviceModel: string | null;
  status: string | null;
  priority: string | null;
  category: string | null;
  adminComments: string | null;
  updatedAt?: string | null;
  attachmentCount: number;
  files: UserFeedbackFileMeta[];
}

export interface UserFeedbackListQuery {
  page?: number;
  pageSize?: number;
  orderBy?: string;
  orderDirection?: 'ASC' | 'DESC';
  searchText?: string;
  status?: string;
  priority?: string;
  category?: string;
  feedbackType?: string;
}

export interface UserFeedbackListResponse {
  data: UserFeedbackRecord[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface UserFeedbackPatch {
  status?: string;
  priority?: string | null;
  category?: string | null;
  adminComments?: string | null;
  /** Style-126 `updated_at` from the save being undone. */
  expectedUpdatedAt?: string;
}

function queryString(query: UserFeedbackListQuery): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === '') continue;
    params.set(key, String(value));
  }
  const text = params.toString();
  return text === '' ? '' : `?${text}`;
}

export function listUserFeedback(
  query: UserFeedbackListQuery,
): Promise<UserFeedbackListResponse> {
  return api.get<UserFeedbackListResponse>(`/user-feedback${queryString(query)}`);
}

export function getUserFeedback(feedbackId: number): Promise<UserFeedbackRecord> {
  return api.get<UserFeedbackRecord>(`/user-feedback/${feedbackId}`);
}

export function saveUserFeedback(
  feedbackId: number,
  patch: UserFeedbackPatch,
): Promise<UserFeedbackRecord> {
  return api.patch<UserFeedbackRecord>(`/user-feedback/${feedbackId}`, patch);
}

export function getUserFeedbackFile(
  feedbackId: number,
  fileId: number,
): Promise<{ fileName: string; mimeType: string; fileData: string }> {
  return api.get(`/user-feedback/${feedbackId}/files/${fileId}`);
}

export function sendUserFeedback(
  input: SendUserFeedbackInput,
): Promise<{ feedbackId: number }> {
  return api.post<{ feedbackId: number }>(
    '/user-feedback',
    {
      subject: input.subject,
      description: input.description,
      platform: input.platform,
      deviceModel: input.deviceModel,
      files: input.files ?? [],
    },
    { timeout: 300000 },
  );
}
